import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import { tankerkoenigService } from '../services/tankerkoenigService';

const { FiDroplet, FiRefreshCw, FiMapPin, FiInfo } = FiIcons;

export default function FuelPrices() {
  const { familyId } = useAuth();
  const [config, setConfig] = useState(null);
  const [stations, setStations] = useState([]);
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [familyId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'tankerkoenig_config')
        .eq('family_id', familyId)
        .maybeSingle();

      if (data?.value) {
        const cfg = data.value;
        setConfig(cfg);
        
        // Fetch all nearby stations to get their info
        const nearby = await tankerkoenigService.getNearbyStations(
          cfg.lat, cfg.lng, cfg.radius, 'all', cfg.apiKey
        );
        
        // Filter for subscribed ones
        const subscribed = nearby.filter(s => cfg.subscribedStations?.includes(s.id));
        setStations(subscribed);

        // Fetch current prices
        const currentPrices = await tankerkoenigService.getStationPrices(cfg.subscribedStations || [], cfg.apiKey);
        setPrices(currentPrices);
      }
    } catch (e) {
      console.error('Error loading fuel prices:', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-20 dark:text-white">Lade Spritpreise...</div>;
  if (!config?.enabled) return <div className="text-center py-20 dark:text-white">Spritpreise sind deaktiviert.</div>;

  return (
    <div className="space-y-8 pb-10">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Spritpreise</h1>
          <p className="text-gray-600 dark:text-gray-400">Deine abonnierten Tankstellen im Überblick</p>
        </div>
        <button
          onClick={loadData}
          className="p-2 bg-white rounded-xl shadow-sm border border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
        >
          <SafeIcon icon={FiRefreshCw} className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stations.map((station) => {
          const stationPrice = prices[station.id];
          const isOpen = stationPrice?.status === 'open';

          return (
            <motion.div
              key={station.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden dark:bg-gray-800 dark:border-gray-700"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 dark:bg-orange-900/30">
                      <SafeIcon icon={FiDroplet} className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white">{station.brand}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{station.place}</p>
                    </div>
                  </div>
                  <div className={`text-xs px-2 py-1 rounded-full font-medium ${
                    isOpen ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {isOpen ? 'Geöffnet' : 'Geschlossen'}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-4">
                  {config.displayFuelTypes?.map(type => (
                    <div key={type} className="bg-gray-50 p-2 rounded-xl dark:bg-gray-700/50 text-center">
                      <div className="text-[10px] font-bold text-gray-500 uppercase dark:text-gray-400 mb-1">{type}</div>
                      <div className="text-lg font-black text-blue-600 dark:text-blue-400">
                        {stationPrice?.[type] ? stationPrice[type].toFixed(3) : '--'}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center text-[10px] text-gray-500 dark:text-gray-400">
                  <SafeIcon icon={FiMapPin} className="w-3 h-3 mr-1" />
                  {station.street} {station.houseNumber} • {station.dist} km
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {stations.length === 0 && (
        <div className="text-center py-10 bg-gray-50 rounded-2xl border border-dashed border-gray-300 dark:bg-gray-800 dark:border-gray-700">
          <p className="text-gray-500 dark:text-gray-400">Keine abonnierten Tankstellen gefunden.</p>
          <p className="text-sm text-gray-400 mt-1">Konfiguriere deine Favoriten in den Einstellungen.</p>
        </div>
      )}
    </div>
  );
}